import { Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from '../../../../environments/environment';

/** Place types that represent a physical, selectable address (not just a road segment). */
const ADDRESS_PLACE_TYPES = ['street_address', 'premise', 'subpremise', 'route'];

/** Matches the trailing country segment Google appends (e.g. ", USA" / ", United States"). */
const TRAILING_US_COUNTRY = /,\s*(USA|United States)\s*$/i;

/**
 * Unit designator, then an identifier.
 *
 * Designators are whole words: `(?![a-z])` stops "Ste" from matching street names
 * that merely start with those letters (Stewart, Sterling, Steele). Digits may
 * follow immediately ("Suite100") because a letter/digit boundary is not a word
 * boundary. Identifiers allow letters, digits, and hyphenated segments
 * (#1, #1A, Apt B, Unit 2-B, Suite 100-A).
 */
const UNIT_PATTERN =
  /(?:^|[\s,]|(?=#))((?:#\s*|(?:apartment|suite|unit|apt|ste)(?![a-z])\.?\s*)[a-z0-9]+(?:-[a-z0-9]+)*)(?=$|[\s,.])/i;

const UNIT_DESIGNATOR_PREFIX =
  /^(?:#\s*|(?:apartment|suite|unit|apt|ste)(?![a-z])\.?\s*)/i;

export interface AddressSuggestion {
  placeId: string;
  label: string;
}

/** Since results are restricted to `includedRegionCodes: ['us']`, the country is redundant. */
function stripCountry(formattedAddress: string): string {
  return formattedAddress.replace(TRAILING_US_COUNTRY, '').trim();
}

/**
 * Extracts a unit designator (e.g. "#1", "Unit 2A", "Apt 5") from an address string.
 * Returns null if no unit is found.
 */
export function extractUnit(address: string): string | null {
  const match = address.match(UNIT_PATTERN);
  return match ? match[1].trim() : null;
}

/**
 * Normalizes a unit string to use "#" prefix consistently.
 * E.g. "Unit 1" → "#1", "Apt B" → "#B", "Suite 100" → "#100", "#A-101" → "#A-101"
 */
function normalizeUnit(unit: string): string {
  const cleaned = unit.replace(UNIT_DESIGNATOR_PREFIX, '').trim();
  return `#${cleaned}`;
}

/**
 * Checks if an address already contains a unit designator.
 */
function hasUnit(address: string): boolean {
  return extractUnit(address) !== null;
}

/**
 * Merges a unit from user input into a selected suggestion if the suggestion
 * doesn't already include a unit. Inserts the unit after the street number/name
 * and before the city portion.
 *
 * E.g. userInput: "2608 E Clark St #1, Tampa, FL"
 *      suggestion: "2608 East Clark Street, Tampa, FL 33605"
 *      result: "2608 East Clark Street #1, Tampa, FL 33605"
 */
export function mergeUnitIntoAddress(
  suggestionLabel: string,
  userInput: string,
): string {
  const userUnit = extractUnit(userInput);

  if (!userUnit || hasUnit(suggestionLabel)) {
    return suggestionLabel;
  }

  const normalizedUnit = normalizeUnit(userUnit);

  const commaIndex = suggestionLabel.indexOf(',');
  if (commaIndex === -1) {
    return `${suggestionLabel} ${normalizedUnit}`;
  }

  const streetPart = suggestionLabel.slice(0, commaIndex);
  const rest = suggestionLabel.slice(commaIndex);
  return `${streetPart} ${normalizedUnit}${rest}`;
}

/**
 * Suggestion labels shown in the dropdown. Places Autocomplete often omits the
 * subpremise ("#1", "Apt B", "Unit 2A"), so the unit is copied from what the
 * user typed when the prediction left it out. A unit Google already returned
 * is left untouched.
 */
export function applyUnitToSuggestions(
  suggestions: readonly AddressSuggestion[],
  userInput: string,
): AddressSuggestion[] {
  return suggestions.map((suggestion) => ({
    placeId: suggestion.placeId,
    label: mergeUnitIntoAddress(suggestion.label, userInput),
  }));
}

/**
 * Wraps the Google Maps JavaScript API's Places library (Autocomplete (New)) to provide
 * debounce-friendly, promise-based address suggestions for `tandem-form-address-autocomplete`.
 *
 * The Maps JS script is lazy-loaded on first use (not on app bootstrap) and cached, so pages
 * that never show an address field never pay the script's load cost.
 *
 * Only the formatted address string + place id are returned — no Place Details request is
 * made, since the form only needs the address text. Prediction text is returned as Google
 * sent it (country stripped). Callers add a typed unit via `applyUnitToSuggestions`; doing
 * that here would freeze the unit to the query that started the request.
 */
@Injectable({
  providedIn: 'root',
})
export class AddressAutocompleteService {
  private placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null;
  private sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

  private getPlacesLibrary(): Promise<google.maps.PlacesLibrary> {
    if (!this.placesLibraryPromise) {
      const loader = new Loader({
        apiKey: environment.googleMapsApiKey,
        version: 'weekly',
      });
      this.placesLibraryPromise = loader.importLibrary('places');
    }
    return this.placesLibraryPromise;
  }

  /**
   * Fetches address suggestions for the given query. Requests made within the same
   * "session" (i.e. between calls to `endSession`) share a session token, per Google's
   * recommended Autocomplete (New) usage pattern.
   */
  async search(query: string): Promise<AddressSuggestion[]> {
    const trimmed = query.trim();
    if (!trimmed) {
      return [];
    }

    const places = await this.getPlacesLibrary();

    if (!this.sessionToken) {
      this.sessionToken = new places.AutocompleteSessionToken();
    }

    const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: trimmed,
      includedRegionCodes: ['us'],
      includedPrimaryTypes: ADDRESS_PLACE_TYPES,
      sessionToken: this.sessionToken,
    });

    return suggestions
      .map((suggestion) => suggestion.placePrediction)
      .filter((prediction): prediction is google.maps.places.PlacePrediction => prediction !== null)
      .map((prediction) => ({
        placeId: prediction.placeId,
        label: stripCountry(prediction.text.text),
      }));
  }

  /** Call once a suggestion is selected (or the field is abandoned) to start a fresh session next time. */
  endSession(): void {
    this.sessionToken = null;
  }
}
